import axios from 'axios';
import { DOMAIN } from '../redux/constants';
import buildAuthHeaders from './apiHeaders';

// dmApi — the ONLY module that knows the DM endpoint shapes.
//
// The backend half shipped concurrently on this same branch. Every path and
// header below is verified against `backend/src/routes/dm.ts` and
// `backend/index.ts` in this worktree, NOT against the plan doc - the plan's
// §3 table lists paths as `/dm/...`, but the router actually mounts under
// `/api/v1/engagement` (index.ts:183). Keeping the calls isolated here means
// any further drift is a one-file fix rather than a sweep through four
// components. Callers deal in the returned plain objects only.
//
// NOTE: the DM endpoints do not exist on `main` yet - they land with this
// branch. Against a `main` backend every call here 404s and the X-Dm-* /
// X-Next-Cursor headers read as null. That is the environment, not a bug in
// this file.
//
// Auth headers come from buildAuthHeaders, NOT a hand-rolled builder. The
// access token lives at `userInfo.access`; reading `userInfo.token` yields
// undefined, every request goes out unauthenticated, 401s, trips the axios
// refresh interceptor and retries - an unbounded request storm. That has
// already burned two features in this repo (see socialActions.js).

// Verified: `app.use('/api/v1/engagement', authRateLimiter, dmRoutes)` at
// backend/index.ts:183, with routes registered as '/dm/conversations/' etc.
const BASE = `${DOMAIN}/api/v1/engagement/dm`;
// The one path NOT under /dm/ - dm.ts:1150 registers a bare '/report-message/'.
const REPORT_MESSAGE_URL = `${DOMAIN}/api/v1/engagement/report-message/`;

// Max body length, mirrored from the backend's 2000-char cap. Enforced in the
// composer so the user sees the limit rather than a 400.
export const MAX_MESSAGE_LENGTH = 2000;

// Reason allowlist for reporting a message. The server enforces this exact
// list and the match is CASE-SENSITIVE ('harassment' is rejected), so these
// strings must stay byte-identical to the backend's - any label added here
// without a matching backend change 400s with {detail, valid_reasons}.
// Identical to ReportDialog's list (engagement's VALID_REPORT_REASONS).
export const REPORT_REASONS = [
  'Inappropriate content',
  'Spam',
  'Harassment',
  'Other',
];

const asArray = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

// Header names are case-insensitive on the wire, but axios lowercases them.
const nextCursor = (res) => res?.headers?.['x-next-cursor'] || null;

/**
 * Read the conversation gate flags off a messages response.
 *
 * They travel as headers because that endpoint returns a bare array and an
 * envelope would break pagination. Returns null when the state header is
 * absent, which is the honest answer for "we do not know" - and null makes
 * the composer default to LOCKED rather than silently open. That matters:
 * these headers read as null cross-origin unless they are in the CORS
 * exposedHeaders allowlist, so a misconfiguration must fail closed.
 */
const dmFlagsFromHeaders = (res) => {
  const h = res?.headers || {};
  const state = h['x-dm-state'];
  if (!state) return null;
  return {
    state,
    can_send: h['x-dm-can-send'] === 'true',
    is_requester: h['x-dm-is-requester'] === 'true',
    needs_action: h['x-dm-needs-action'] === 'true',
  };
};

/**
 * List conversations for one state.
 *
 * `state` partitions the inbox: 'accepted' is the real inbox, 'pending' is
 * the message-requests tray. They are separate fetches rather than one list
 * filtered client-side, so an inbox full of requests can never push real
 * conversations off the first page.
 *
 * Returns { conversations, cursor } - cursor is null when exhausted.
 */
export const listConversations = async (userInfo, { state = 'accepted', cursor, limit = 30 } = {}) => {
  const params = new URLSearchParams({ state, limit: String(limit) });
  if (cursor) params.set('cursor', cursor);
  const res = await axios.get(`${BASE}/conversations/?${params}`, buildAuthHeaders(userInfo));
  return { conversations: asArray(res.data), cursor: nextCursor(res) };
};

/**
 * Open (or find) the 1:1 conversation with `username`.
 *
 * Returns 201 in ALL cases - created, already existed, and lost-the-race. Do
 * NOT branch on 200-vs-201 to infer whether the thread is new: a split status
 * leaked that an earlier request had been declined, which defeats silent
 * decline. Read `state` / `can_send` / `needs_action` instead.
 */
export const openConversation = async (userInfo, username) => {
  const res = await axios.post(`${BASE}/conversations/`, { username }, buildAuthHeaders(userInfo));
  return res.data;
};

/**
 * Fetch one conversation: gate flags AND identity in a single call.
 *
 * This is what the thread view needs. The X-Dm-* headers on the messages
 * endpoint carry authoritative flags but no `other_user`, so without this
 * route the only way to get a username and avatar was to page both
 * conversation lists hunting for an id.
 *
 * 404 is the uniform no-probe response for non-participants, identical to
 * every other conversation-keyed route.
 */
export const fetchConversation = async (userInfo, conversationId) => {
  const res = await axios.get(`${BASE}/conversations/${conversationId}/`, buildAuthHeaders(userInfo));
  return res.data;
};

/**
 * Fetch a page of messages.
 *
 * Keyset pagination on (createdAt, id): `before` walks backwards into
 * history, `after` picks up messages newer than one we already hold. There
 * is no offset/page param by design - offsets skip or duplicate rows when
 * messages arrive mid-scroll.
 *
 * Returns { messages, cursor } with messages in server order.
 */
export const fetchMessages = async (userInfo, conversationId, { before, after, limit = 30 } = {}) => {
  const params = new URLSearchParams({ limit: String(limit) });
  if (before) params.set('before', before);
  if (after) params.set('after', after);
  const res = await axios.get(
    `${BASE}/conversations/${conversationId}/messages/?${params}`,
    buildAuthHeaders(userInfo),
  );
  // The gate flags ride as HEADERS, not in the body: this endpoint's contract
  // is a bare array, and wrapping it in an envelope would break pagination.
  // All four are CORS-exposed (backend/index.ts:49-52) - without that they
  // read as null cross-origin even though the server sent them.
  return {
    messages: asArray(res.data),
    cursor: nextCursor(res),
    conversation: dmFlagsFromHeaders(res),
  };
};

/**
 * Send a message.
 *
 * `clientId` is an idempotency key, not a nicety: the backend uniques on
 * (conversationId, clientId), so a retry after a timeout returns the
 * original row instead of double-posting. It is also what lets the optimistic
 * bubble be reconciled with the server echo - see ThreadView's dedup.
 */
export const sendMessage = async (userInfo, conversationId, body, clientId, questionId) => {
  const res = await axios.post(
    `${BASE}/conversations/${conversationId}/messages/`,
    // question_id attaches a structured share (wave 3). The server allows an
    // empty body when it is present - the card is the message.
    { body, client_id: clientId, ...(questionId ? { question_id: questionId } : {}) },
    buildAuthHeaders(userInfo),
  );
  return res.data;
};

/** Accept a pending message request, unlocking replies in both directions. */
export const acceptConversation = async (userInfo, conversationId) => {
  const res = await axios.post(`${BASE}/conversations/${conversationId}/accept/`, {}, buildAuthHeaders(userInfo));
  return res.data;
};

/**
 * Decline a pending message request.
 *
 * DISTINCT FROM `clearConversation`, and the difference is load-bearing.
 * Decline is its own endpoint (dm.ts:982) that resolves the request; DELETE is
 * a per-side *hide*. Using DELETE to decline would hide the thread from the
 * recipient while leaving the request itself live, so the sender would still
 * be holding an open request the recipient can no longer see.
 *
 * The conversation deliberately survives a decline: deleting the row would
 * free the unique pairKey and let the sender re-open the thread, slipping a
 * second message past the one-message-then-wait gate. It simply drops out of
 * both the accepted and pending lists, so the inbox needs no extra filtering.
 */
export const declineConversation = async (userInfo, conversationId) => {
  const res = await axios.post(`${BASE}/conversations/${conversationId}/decline/`, {}, buildAuthHeaders(userInfo));
  return res.data;
};

/**
 * Clear (per-side hide) a conversation. NOT a decline - see above.
 */
export const clearConversation = async (userInfo, conversationId) => {
  const res = await axios.delete(`${BASE}/conversations/${conversationId}/`, buildAuthHeaders(userInfo));
  return res.data;
};

/**
 * Hide ("delete for you") a conversation.
 *
 * THREE different removals now exist on this router, and picking the wrong
 * one is a real bug rather than a style choice:
 *   - declineConversation resolves a pending REQUEST (and is silent to the
 *     sender);
 *   - clearConversation moves this side's message floor, keeping the thread
 *     in the inbox but emptying it;
 *   - this one takes the whole thread out of the viewer's list while leaving
 *     every message intact.
 *
 * It is NOT a delete on the server: the participant row survives, because the
 * pairKey and the one-message-then-wait gate are built on it. The thread
 * reappears in both lists as soon as either side sends - hiding is a filter
 * the next message clears, which is what "Delete for you" means in Messenger.
 */
export const hideConversation = async (userInfo, conversationId) => {
  const res = await axios.post(`${BASE}/conversations/${conversationId}/hide/`, {}, buildAuthHeaders(userInfo));
  return res.data;
};

/**
 * Mute or unmute a conversation.
 *
 * `minutes` is a duration, not a boolean: pass a positive number to mute for
 * that long, or 0/null to unmute. Muting suppresses the BELL only - messages
 * still arrive and still count as unread, because muting means "stop
 * interrupting me", not "hide this from me".
 */
export const muteConversation = async (userInfo, conversationId, minutes) => {
  const res = await axios.post(
    `${BASE}/conversations/${conversationId}/mute/`,
    { minutes: minutes || 0 },
    buildAuthHeaders(userInfo),
  );
  return res.data;
};

/**
 * Unsend (tombstone) one of your OWN messages.
 *
 * The row survives with an empty body - the keyset cursor is (createdAt, id),
 * so deleting it would move a page boundary under a paginating client and
 * silently skip a message. The recipient's open thread drops it via the
 * `dm.message_deleted` push.
 */
export const unsendMessage = async (userInfo, conversationId, messageId) => {
  const res = await axios.delete(
    `${BASE}/conversations/${conversationId}/messages/${messageId}/`,
    buildAuthHeaders(userInfo),
  );
  return res.data;
};

/** Mark a conversation read up to `upToMessageId` (default: everything). */
export const markRead = async (userInfo, conversationId, upToMessageId) => {
  const res = await axios.post(
    `${BASE}/conversations/${conversationId}/read/`,
    upToMessageId ? { up_to_message_id: upToMessageId } : {},
    buildAuthHeaders(userInfo),
  );
  return res.data;
};

/**
 * Unread totals.
 *
 * Two numbers, and the badge uses `unread_conversations`, NOT `unread_total`.
 * A badge counting individual messages turns one chatty friend into a "47"
 * that tells the user nothing about how many threads need attention.
 *
 * Pending threads are excluded server-side, so a stranger cannot put a red
 * dot on your icon by opening a request.
 */
export const fetchUnreadCount = async (userInfo) => {
  const res = await axios.get(`${BASE}/unread-count/`, buildAuthHeaders(userInfo));
  return {
    unreadTotal: Number(res.data?.unread_total) || 0,
    unreadConversations: Number(res.data?.unread_conversations) || 0,
  };
};

/** Report a single message. The body is snapshotted server-side at report time. */
export const reportMessage = async (userInfo, messageId, reason, details) => {
  const res = await axios.post(
    REPORT_MESSAGE_URL,
    { message_id: messageId, reason, details: details || undefined },
    buildAuthHeaders(userInfo),
  );
  return res.data;
};

/**
 * Is this conversation writable by the viewer right now?
 *
 * `can_send` is server-authoritative and mirrors the send handler's own gate
 * exactly, so a locked composer and a rejected send can never disagree. The
 * local fallback exists only for the case where the flag is missing entirely
 * (an unexposed CORS header reads as absent), and it deliberately fails
 * CLOSED on a pending thread rather than guessing open.
 */
export const canSend = (conversation) => {
  if (!conversation) return false;
  if (typeof conversation.can_send === 'boolean') return conversation.can_send;
  if (conversation.state === 'pending') return false;
  return true;
};

/**
 * Is the viewer the person who OPENED a pending thread (vs. the person being
 * asked)? Drives which side of the request UI renders: the requester sees a
 * "waiting to be accepted" notice, the recipient sees Accept / Decline.
 */
export const isRequester = (conversation) => {
  if (!conversation) return false;
  if (typeof conversation.is_requester === 'boolean') return conversation.is_requester;
  // Without the flag, infer from the gate: a pending thread the viewer cannot
  // write to is one they already sent into.
  return conversation.state === 'pending' && !canSend(conversation);
};

/**
 * Does the viewer have a decision to make on this thread?
 *
 * Server-authoritative: `needs_action` is true exactly when Accept / Decline
 * should render. It is NOT the negation of `is_requester` - an accepted thread
 * is neither - so the two flags answer different questions and both are needed.
 */
export const needsAction = (conversation) => {
  if (!conversation) return false;
  if (typeof conversation.needs_action === 'boolean') return conversation.needs_action;
  // Fallback: a pending thread the viewer did NOT open is one awaiting them.
  return conversation.state === 'pending' && !isRequester(conversation);
};

/**
 * Merge incoming messages into an existing list, deduping and re-sorting.
 *
 * DEDUPES ON `id` OR `client_id`, and that OR is the whole point. An
 * optimistic bubble has a client_id and no server id; the echo of our own
 * send (over WS, or as the POST response) has both. Deduping on id alone
 * would fail to match the optimistic row and render the sender's own message
 * twice - the single most likely bug in a thread view, and one that only
 * appears for the sender, so it survives testing from the other side.
 *
 * A matched row is REPLACED IN PLACE rather than removed and appended, so a
 * confirmed message keeps its position instead of jumping to the end.
 *
 * Extracted from the component so it can be tested without mounting a thread.
 */
export const mergeMessageLists = (existing, incoming, { prepend = false } = {}) => {
  if (!incoming || incoming.length === 0) return existing;
  const next = [...existing];
  incoming.forEach((msg) => {
    const at = next.findIndex((m) => (
      (msg.id && m.id === msg.id)
      || (msg.client_id && m.client_id && m.client_id === msg.client_id)
    ));
    if (at >= 0) {
      // The server row wins, but the optimistic flags must never come back:
      // a confirmed message that still reads `pending` stays dimmed forever.
      next[at] = { ...next[at], ...msg, pending: false, failed: false };
    } else if (prepend) {
      next.unshift(msg);
    } else {
      next.push(msg);
    }
  });
  next.sort((a, b) => {
    const ta = new Date(a.created_at || 0).getTime();
    const tb = new Date(b.created_at || 0).getTime();
    // Tie-break on id, mirroring the server's (createdAt, id) keyset. Two
    // messages sent in the same millisecond must not reorder between renders,
    // or bubbles visibly swap places.
    if (ta !== tb) return ta - tb;
    return String(a.id || a.client_id || '').localeCompare(String(b.id || b.client_id || ''));
  });
  return next;
};

/** A stable client id for optimistic sends. */
export const newClientId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `c-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};
